import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlarmClock,
  Check,
  ChevronDown,
  Coffee,
  LogIn,
  LogOut,
  Pause,
  UtensilsCrossed,
} from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Badge } from '@/components/ui-kit/badge';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Captcha, useCaptcha } from '@/components/core';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { BreakEntryDialog } from '../break-entry-dialog/break-entry-dialog';
import {
  useClockIn,
  useClockOut,
  useGetMyFineSummary,
  useEndBreak,
  useGetTodayAttendance,
  useHasPendingCorrectionToday,
  useStartBreak,
} from '../../hooks/use-attendance';
import { useAttendanceRuleContext } from '../../hooks/use-attendance-policy';
import {
  breakTypeOf,
  canSelfClockInToday,
  canTakeLunch,
  chargedBreakSeconds,
  findOpenBreak,
  formatTimeOfDay,
  formatWaitTime,
  getBreakEndBlocker,
  getBreakStartBlocker,
  isOpenRecord,
  isPastClockInCutoff,
  officeNetworkBlocker,
  secondsUntilClockOut,
  sessionStartIso,
  validateDayTotals,
  validateEnteredBreaks,
  visibleBreaks,
} from '../../services/attendance-rules';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { BreakTimeDetail, BreakType } from '../../types/attendance.types';

type ClockPhase = 'out' | 'working' | 'break' | 'done';

/** A running duration as "HH:mm:ss" (the stored totals use `formatDuration`, "HH:mm"). */
const formatElapsed = (totalSeconds: number): string => {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hh = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

/** A clock-in/out instant as "HH:mm:ss" in the zone the day was recorded in (its `Timezone`). */
const formatClockTime = (iso?: string, timeZone?: string): string => {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return '--:--:--';
  return formatTimeOfDay(iso, true, timeZone);
};

/** Time already spent in closed `entries`, for the live display (the open break is added
 * separately; a lunch recorded to its 30-minute minimum counts only up to `now`). */
const sumElapsedSeconds = (entries: BreakTimeDetail[], now: Date): number =>
  entries.reduce((total, entry) => {
    const start = new Date(entry.StartTimeUtc).getTime();
    const end = Math.min(new Date(entry.EndTimeUtc ?? '').getTime(), now.getTime());
    if (!Number.isFinite(start) || !Number.isFinite(end)) return total;
    return total + Math.max(0, Math.floor((end - start) / 1000));
  }, 0);

/** A frosted time tile on the clock card (the old portal's "hero" tiles). */
const ClockTile = ({
  label,
  value,
  loading,
}: {
  label: string;
  value: string;
  loading?: boolean;
}) => (
  <div className="min-w-0 rounded-[9px] border border-clock-hero/20 bg-clock-hero/10 px-3 py-2 max-md:px-2 max-md:py-1.5">
    <div className="break-words text-xs font-semibold uppercase tracking-[0.08em] text-clock-hero/70">
      {label}
    </div>
    {loading ? (
      <span className="mt-1 block h-6 w-16 animate-pulse rounded bg-clock-hero/20" />
    ) : (
      <div className="mt-0.5 truncate text-xl font-semibold tabular-nums max-md:text-[15px]">
        {value}
      </div>
    )}
  </div>
);

/** Left edges of the fine chips, light enough to read on the dark card. */
const FINE_ACCENT = {
  attendance: 'border-l-emerald-300',
  nonCompliance: 'border-l-sky-300',
  lunch: 'border-l-amber-300',
} as const;

/** Fines are summed from float amounts (attendance fines are computed), so show 2 decimals. No
 * currency: neither the fine rule (catalog `CORRECTION_FINE.amount`) nor the Fine records name one. */
const formatFine = (amount: number) => amount.toFixed(2);

const FineStat = ({ label, amount, accent }: { label: string; amount: number; accent: string }) => (
  <div
    className={cn(
      'flex min-w-[140px] items-baseline gap-1.5 whitespace-nowrap rounded-[9px] border border-l-[3px] border-clock-hero/20 bg-clock-hero/10 px-3 py-2 max-md:min-w-0 max-md:justify-between',
      accent
    )}
  >
    <span className="text-sm font-semibold text-clock-hero/70">{label}:</span>
    <span className="text-base font-bold">{formatFine(amount)}</span>
  </div>
);

/** The clock card's buttons: 44px, white on the dark card (outlined for breaks), as the old portal. */
const HERO_BUTTON =
  'h-11 rounded-lg px-[18px] text-[15px] font-bold disabled:border-transparent disabled:bg-clock-hero/15 disabled:text-clock-hero/50 disabled:opacity-100 max-md:h-[39px] max-md:px-3 max-md:text-xs';
const HERO_BUTTON_VARIANT = {
  primary: 'bg-clock-hero text-clock-hero-from hover:bg-clock-hero/90 hover:text-clock-hero-from',
  ghost:
    'border border-clock-hero/50 bg-transparent font-semibold text-clock-hero hover:bg-clock-hero/10 hover:text-clock-hero',
  danger: 'bg-clock-hero text-error hover:bg-clock-hero/90 hover:text-error',
} as const;

export const ClockWidget = () => {
  const { t } = useTranslation();
  const [isConfirmingClockOut, setIsConfirmingClockOut] = useState(false);
  const [isFineExpanded, setIsFineExpanded] = useState(false);
  const [isBreakEntryOpen, setIsBreakEntryOpen] = useState(false);
  const [pendingBreakDetails, setPendingBreakDetails] = useState<BreakTimeDetail[]>([]);
  const [, forceTick] = useState(0);

  // Every rule below comes from the permissions the user holds (see `useAttendancePolicy`),
  // never from their country. The workflows re-check all of them; this only decides what to offer.
  const context = useAttendanceRuleContext();
  const { policy } = context;
  const canUseBreaks = policy.canTakeBreak || canTakeLunch(policy);

  // CAPTCHA_ON_CLOCK_OUT: the clock-out workflow verifies the token with Google before saving.
  const { toast } = useToast();
  const needsCaptcha = Boolean(policy.rules.clockOutCaptcha);
  // The app has no other captcha (sign-in is SSO only), so this key belongs to clock-out alone.
  const captchaSiteKey = import.meta.env.VITE_ATTENDANCE_CAPTCHA_SITE_KEY || '';
  const {
    code: captchaToken,
    captcha,
    reset: resetCaptcha,
  } = useCaptcha({ siteKey: captchaSiteKey, type: 'reCaptcha-v2-checkbox' });

  const { data: attendance, isLoading } = useGetTodayAttendance();
  const { data: fineSummary, isLoading: isFineLoading } = useGetMyFineSummary();

  const { mutateAsync: clockIn, isPending: isClockingIn } = useClockIn();
  const { mutateAsync: clockOut, isPending: isClockingOut } = useClockOut();
  const {
    mutateAsync: startBreak,
    isPending: isStartingBreak,
    variables: startBreakVariables,
  } = useStartBreak();
  const { mutateAsync: endBreak, isPending: isEndingBreak } = useEndBreak();
  const isSavingBreak = isStartingBreak || isEndingBreak;
  // Only the clicked break button spins; the others are just disabled while it runs.
  const startingBreakType = isStartingBreak ? startBreakVariables?.type : undefined;

  const now = new Date();
  const allEntries = attendance?.BreakTimeDetails ?? [];
  // Breaks the employee took; the gaps between sessions (MULTIPLE_SESSIONS) are not breaks.
  const breaks = visibleBreaks(allEntries);
  const gaps = allEntries.filter((entry) => entry.Type === 'ClockOut');
  const hasClockedIn = Boolean(attendance?.StartTimeUtc);
  const isOpen = isOpenRecord(attendance);
  // The WFO office-day and cutoff rules hide Clock In rather than disable it, like `!canClock`,
  // since there's nothing the employee can do about either from this screen.
  const canClockInToday = canSelfClockInToday(context.isWFH, context.wfoDays, now);
  // A correction of today waiting for review: no clock-in until it is reviewed (the workflow refuses).
  const { hasPending: hasPendingCorrectionToday, isLoading: isPendingCheckLoading } =
    useHasPendingCorrectionToday();
  const isPastCutoff = isPastClockInCutoff(policy, now);
  const networkBlocker = officeNetworkBlocker(policy, context.isWFH);
  // The open break (Start with no End yet) is the single source of truth for "currently on
  // break" — it's written the moment a break starts, so a refresh re-reads it from `attendance`.
  const openBreak = findOpenBreak(breaks);
  const isOnBreak = Boolean(openBreak);
  const isOnLunchBreak = Boolean(openBreak && breakTypeOf(openBreak) === 'Lunch');
  const phase: ClockPhase = !hasClockedIn
    ? 'out'
    : !isOpen
      ? 'done'
      : isOnBreak
        ? 'break'
        : 'working';
  const canClockInAgain =
    phase === 'done' &&
    policy.multipleSessions &&
    policy.canClock &&
    !attendance?.IsForceClockOut &&
    canClockInToday &&
    !hasPendingCorrectionToday &&
    !isPastCutoff;

  const clockOutWaitSeconds =
    attendance && isOpen ? secondsUntilClockOut(policy, sessionStartIso(attendance), now) : 0;
  const endBreakBlocker = openBreak ? getBreakEndBlocker(policy, openBreak, now) : null;
  const regularBreakBlocker = attendance
    ? getBreakStartBlocker(policy, 'Regular', attendance, now)
    : null;
  const lunchBreakBlocker = attendance
    ? getBreakStartBlocker(policy, 'Lunch', attendance, now)
    : null;
  // Why the lunch button is missing, when the employee could take lunch at another time today.
  const lunchHint =
    canTakeLunch(policy) &&
    !isOnBreak &&
    (lunchBreakBlocker === 'LUNCH_BREAK_NOT_AVAILABLE_YET' ||
      lunchBreakBlocker === 'LUNCH_BREAK_WINDOW_CLOSED')
      ? lunchBreakBlocker
      : null;

  useEffect(() => {
    if (phase === 'working' || phase === 'break') {
      const id = setInterval(() => forceTick((tick) => tick + 1), 1000);
      return () => clearInterval(id);
    }
    // Before clocking in (or again) nothing needs a per-second tick, but the cutoff rule is
    // time-based: a Clock In button shown at 15:59 must disappear once 16:00 passes.
    if (phase === 'out' || phase === 'done') {
      const id = setInterval(() => forceTick((tick) => tick + 1), 60 * 1000);
      return () => clearInterval(id);
    }
    return undefined;
  }, [phase]);

  const completedBreakSeconds = sumElapsedSeconds(breaks, now);
  const gapSeconds = sumElapsedSeconds(gaps, now);

  const workedSeconds = (() => {
    if (!attendance?.StartTimeUtc) return 0;
    const startMs = new Date(attendance.StartTimeUtc).getTime();
    if (phase === 'done' && attendance.TotalWorkTimeInSecond != null) {
      // The stored total already includes any counted-but-not-taken lunch minimum.
      return attendance.TotalWorkTimeInSecond;
    }
    const untilMs =
      phase === 'break' && openBreak
        ? new Date(openBreak.StartTimeUtc).getTime()
        : phase === 'done' && attendance.EndTimeUtc
          ? new Date(attendance.EndTimeUtc).getTime()
          : now.getTime();
    return Math.max(0, Math.floor((untilMs - startMs) / 1000) - gapSeconds - completedBreakSeconds);
  })();

  const breakSeconds =
    phase === 'break' && openBreak
      ? completedBreakSeconds +
        Math.max(0, Math.floor((now.getTime() - new Date(openBreak.StartTimeUtc).getTime()) / 1000))
      : phase === 'done' && attendance?.TotalBreakTimeInSecond
        ? attendance.TotalBreakTimeInSecond
        : completedBreakSeconds;

  const handleClockIn = async () => {
    try {
      await clockIn();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  const handleStartBreak = async (type: BreakType) => {
    try {
      await startBreak({ type });
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  const handleEndBreak = async () => {
    try {
      await endBreak();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  // An employee who takes regular breaks but logged none today (forgot to click Take Break/End
  // Break live) gets one chance to enter them before clocking out. Lunch-only holders skip this:
  // their lunch rule decides what is recorded at clock-out.
  const handleClockOutClick = () => {
    if (policy.canTakeBreak && breaks.length === 0) setIsBreakEntryOpen(true);
    else setIsConfirmingClockOut(true);
  };

  const handleBreakEntryConfirm = (entered: BreakTimeDetail[]) => {
    setPendingBreakDetails(entered);
    setIsConfirmingClockOut(true);
  };

  const handleBreakEntrySkip = () => {
    setPendingBreakDetails([]);
    setIsConfirmingClockOut(true);
  };

  /** Checks breaks typed into the missed-break dialog against the same rules clock-out applies,
   * so the dialog can show the problem instead of the clock-out failing after it closes. */
  const validateEnteredBreakList = (entered: BreakTimeDetail[]): string | null => {
    if (!attendance?.StartTimeUtc) return null;
    const clockInAt = new Date(attendance.StartTimeUtc);
    const at = new Date();
    const invalid = validateEnteredBreaks(policy, entered, clockInAt, at);
    if (invalid) return invalid;
    const clockSeconds = (at.getTime() - clockInAt.getTime()) / 1000 - gapSeconds;
    const charged = chargedBreakSeconds(policy, entered);
    return validateDayTotals(policy, clockSeconds - charged, charged, false);
  };

  const handleClockOut = async () => {
    if (!attendance?.StartTimeUtc || isClockingOut) return;
    if (needsCaptcha && !captchaToken) {
      toast({
        variant: 'destructive',
        title: t(captchaSiteKey ? 'CAPTCHA_REQUIRED' : 'CAPTCHA_NOT_CONFIGURED'),
      });
      return;
    }
    try {
      await clockOut({
        enteredBreaks: pendingBreakDetails,
        ...(needsCaptcha ? { captchaToken } : {}),
      });
      setIsConfirmingClockOut(false);
      // Kept on a refusal, so a retry (e.g. after a new captcha) still sends the entered breaks.
      setPendingBreakDetails([]);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    } finally {
      // A token is good for one verification only.
      if (needsCaptcha) resetCaptcha();
    }
  };

  const statusBadge =
    phase === 'working' || phase === 'break' ? (
      <span
        className={cn(
          'inline-flex h-[26px] flex-none items-center gap-[7px] whitespace-nowrap rounded-full px-3 text-xs font-bold uppercase tracking-[0.06em]',
          phase === 'break'
            ? 'bg-yellow-400/90 text-yellow-950'
            : 'bg-emerald-500/90 text-emerald-950'
        )}
      >
        <span className="size-[7px] animate-pulse rounded-full bg-current" />
        {phase === 'break' ? t(isOnLunchBreak ? 'ON_LUNCH_BREAK' : 'ON_BREAK') : t('WORKING')}
      </span>
    ) : null;

  // Until today's record, the employee's rules and the pending-correction check are in, the phase and
  // which buttons apply are unknown: placeholders instead of buttons that would change a moment later.
  const isClockStateLoading = isLoading || context.isLoading || isPendingCheckLoading;

  const showClockIn =
    !hasClockedIn &&
    !context.isLoading &&
    policy.canClock &&
    canClockInToday &&
    !hasPendingCorrectionToday &&
    !isPastCutoff &&
    !networkBlocker;
  const showEndBreak = isOpen && isOnBreak;
  const showTakeBreak = isOpen && !isOnBreak && !regularBreakBlocker;
  const showTakeLunch = isOpen && !isOnBreak && !lunchBreakBlocker;
  // Any running break, lunch included, is ended first (the clock-out workflow refuses otherwise).
  const showClockOut = isOpen && policy.canClock && !isOnBreak && clockOutWaitSeconds === 0;
  // As the old portal: break buttons share the row; Clock In / Clock Out alone take half of it.
  const hasBreakButton = showEndBreak || showTakeBreak || showTakeLunch;
  const soloWidth = 'shrink-0 basis-1/2 max-md:shrink';

  const hints = [
    !policy.canClock && phase !== 'done' && t('ATTENDANCE_NO_CLOCK_PERMISSION'),
    policy.canClock && networkBlocker && (phase !== 'done' || canClockInAgain) && t(networkBlocker),
    policy.canClock &&
      !hasClockedIn &&
      !canClockInToday &&
      t('ATTENDANCE_OFFICE_DAY_NO_SELF_CLOCK_IN'),
    policy.canClock &&
      hasPendingCorrectionToday &&
      (!hasClockedIn || (phase === 'done' && policy.multipleSessions)) &&
      t('ATTENDANCE_CORRECTION_PENDING_FOR_TODAY'),
    policy.canClock &&
      !hasClockedIn &&
      canClockInToday &&
      isPastCutoff &&
      t('ATTENDANCE_CLOCK_IN_CUTOFF_PASSED'),
    policy.canClock &&
      phase === 'working' &&
      clockOutWaitSeconds > 0 &&
      t('ATTENDANCE_CLOCK_OUT_AVAILABLE_IN', { time: formatWaitTime(clockOutWaitSeconds) }),
    phase === 'break' && endBreakBlocker && t(endBreakBlocker),
    phase === 'working' && lunchHint && t(lunchHint),
  ].filter((hint): hint is string => Boolean(hint));

  const fineTotal = fineSummary && (
    <span className="flex flex-wrap items-baseline gap-1.5">
      <span className="text-base font-semibold text-clock-hero/85">
        {t('CURRENT_HR_YEAR_FINE')}:
      </span>
      <span className="text-xl font-bold">{formatFine(fineSummary.grandTotal)}</span>
    </span>
  );

  return (
    <>
      {/* The old portal's "hero": a dark blue panel in both themes (its own `clock-hero` colours:
          the theme's `white` and primary shades flip in dark mode), the day and its running timer
          on the left, the times and actions on the right; one column on a phone. */}
      <section className="rounded-xl bg-[linear-gradient(105deg,var(--tw-gradient-stops))] from-clock-hero-from via-clock-hero-via via-55% to-clock-hero-to px-6 py-5 text-clock-hero shadow-sm max-md:rounded-[10px] max-md:p-4">
        <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[4fr_3fr] md:gap-6">
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex items-center gap-3 max-md:justify-between">
              <span className="min-w-0 break-words text-[23px] font-medium text-clock-hero/80 max-md:text-base">
                {new Date().toLocaleDateString(undefined, {
                  timeZone: getOrganizationTimeZone(),
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
              {statusBadge}
            </div>
            <div className="max-md:text-center">
              <div className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.1em] text-clock-hero/60 max-md:justify-center">
                {phase === 'break' && <Pause className="size-3.5" />}
                {phase === 'done' ? t('TOTAL_WORKED') : t('WORKED_TODAY')}
              </div>
              {isLoading ? (
                <span className="mt-1 block h-12 w-56 animate-pulse rounded bg-clock-hero/20 max-md:mx-auto max-md:h-8 max-md:w-40" />
              ) : (
                <div className="mt-1 text-5xl font-bold leading-none tabular-nums tracking-[0.01em] max-md:text-[32px]">
                  {formatElapsed(workedSeconds)}
                </div>
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            <div
              className={cn(
                'grid gap-2 max-md:gap-1.5',
                canUseBreaks ? 'grid-cols-3' : 'grid-cols-2'
              )}
            >
              <ClockTile
                label={t('CLOCK_IN_TIME')}
                value={formatClockTime(attendance?.StartTimeUtc, attendance?.Timezone)}
                loading={isLoading}
              />
              {canUseBreaks && (
                <ClockTile
                  label={t(phase === 'break' ? 'TOTAL_BREAK' : 'BREAK_TIME')}
                  value={breakSeconds > 0 ? formatElapsed(breakSeconds) : '--:--:--'}
                  loading={isLoading || isSavingBreak}
                />
              )}
              <ClockTile
                label={t('CLOCK_OUT_TIME')}
                value={formatClockTime(attendance?.EndTimeUtc, attendance?.Timezone)}
                loading={isLoading}
              />
            </div>

            {phase === 'done' && (
              <>
                <div className="flex flex-wrap items-center justify-center gap-3 rounded-[9px] border border-emerald-400/40 bg-emerald-400/15 px-4 py-3.5">
                  <span className="flex size-8 flex-none items-center justify-center rounded-full bg-emerald-500 text-clock-hero">
                    <Check className="size-[18px]" />
                  </span>
                  <span className="text-xl font-bold max-md:text-base">
                    {t(attendance?.IsForceClockOut ? 'DAY_CLOSED_AUTOMATICALLY' : 'DAY_COMPLETED')}
                  </span>
                  {attendance?.Status && (
                    <Badge variant="outline" className="border-clock-hero/50 text-clock-hero">
                      {t(`DAY_STATUS_${attendance.Status}`)}
                    </Badge>
                  )}
                </div>
                {canClockInAgain && !networkBlocker && (
                  <div className="flex justify-end max-md:justify-center">
                    <Button
                      onClick={handleClockIn}
                      loading={isClockingIn}
                      disabled={isClockingIn}
                      className={cn(HERO_BUTTON, HERO_BUTTON_VARIANT.primary, soloWidth)}
                    >
                      <LogIn />
                      {t('CLOCK_IN_AGAIN')}
                    </Button>
                  </div>
                )}
              </>
            )}

            {isClockStateLoading && (
              <div className="flex justify-end max-md:justify-center">
                <span
                  className={cn('block h-11 animate-pulse rounded-lg bg-clock-hero/20', soloWidth)}
                />
              </div>
            )}

            {!isClockStateLoading && phase !== 'done' && (
              <div className="flex flex-wrap items-center justify-end gap-2.5 max-md:justify-center">
                {showClockIn && (
                  <Button
                    onClick={handleClockIn}
                    loading={isClockingIn}
                    disabled={isClockingIn}
                    className={cn(HERO_BUTTON, HERO_BUTTON_VARIANT.primary, soloWidth)}
                  >
                    <AlarmClock />
                    {t('CLOCK_IN')}
                  </Button>
                )}

                {showEndBreak && (
                  <Button
                    onClick={handleEndBreak}
                    loading={isEndingBreak}
                    disabled={isSavingBreak || Boolean(endBreakBlocker)}
                    className={cn(HERO_BUTTON, HERO_BUTTON_VARIANT.ghost, 'flex-1')}
                  >
                    <Coffee />
                    {t('END_BREAK')}
                  </Button>
                )}

                {showTakeBreak && (
                  <Button
                    onClick={() => handleStartBreak('Regular')}
                    loading={startingBreakType === 'Regular'}
                    disabled={isSavingBreak}
                    className={cn(HERO_BUTTON, HERO_BUTTON_VARIANT.ghost, 'flex-1')}
                  >
                    <Coffee />
                    {t('TAKE_BREAK')}
                  </Button>
                )}

                {showTakeLunch && (
                  <Button
                    onClick={() => handleStartBreak('Lunch')}
                    loading={startingBreakType === 'Lunch'}
                    disabled={isSavingBreak}
                    className={cn(HERO_BUTTON, HERO_BUTTON_VARIANT.ghost, 'flex-1')}
                  >
                    <UtensilsCrossed />
                    {t('TAKE_LUNCH_BREAK')}
                  </Button>
                )}

                {showClockOut && (
                  <Button
                    onClick={handleClockOutClick}
                    disabled={isClockingOut}
                    className={cn(
                      HERO_BUTTON,
                      HERO_BUTTON_VARIANT.danger,
                      hasBreakButton ? 'flex-1' : soloWidth
                    )}
                  >
                    <LogOut />
                    {t('CLOCK_OUT')}
                  </Button>
                )}
              </div>
            )}

            {!isClockStateLoading &&
              hints.map((hint) => (
                <p key={hint} className="text-[13px] font-medium text-clock-hero/60">
                  {hint}
                </p>
              ))}
          </div>
        </div>

        {fineSummary && (
          <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-clock-hero/15 pt-3 max-md:mt-2 max-md:flex-col max-md:items-stretch">
            <div className="max-md:hidden">{fineTotal}</div>
            {/* On a phone the total is a toggle that opens the breakdown, as in the old portal. */}
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 rounded-[9px] border border-clock-hero/20 bg-clock-hero/10 px-3 py-2 text-left md:hidden"
              aria-expanded={isFineExpanded}
              aria-label={t(isFineExpanded ? 'SHOW_LESS' : 'SHOW_MORE')}
              onClick={() => setIsFineExpanded((expanded) => !expanded)}
            >
              <span className="min-w-0 flex-1">{fineTotal}</span>
              <ChevronDown
                className={cn(
                  'size-[18px] flex-none transition-transform',
                  isFineExpanded && 'rotate-180'
                )}
              />
            </button>
            <div
              className={cn(
                'flex flex-1 flex-wrap justify-end gap-2.5',
                'max-md:flex-col max-md:justify-start',
                !isFineExpanded && 'max-md:hidden'
              )}
            >
              <FineStat
                label={t('ATTENDANCE')}
                amount={fineSummary.attendance}
                accent={FINE_ACCENT.attendance}
              />
              <FineStat
                label={t('NON_COMPLIANCE')}
                amount={fineSummary.nonCompliance}
                accent={FINE_ACCENT.nonCompliance}
              />
              <FineStat label={t('LUNCH')} amount={fineSummary.lunch} accent={FINE_ACCENT.lunch} />
            </div>
          </div>
        )}
        {isFineLoading && !fineSummary && (
          <div className="mt-3 border-t border-clock-hero/15 pt-3">
            <span className="block h-6 w-56 animate-pulse rounded bg-clock-hero/20" />
          </div>
        )}
      </section>

      <BreakEntryDialog
        open={isBreakEntryOpen}
        onOpenChange={setIsBreakEntryOpen}
        day={attendance?.StartTimeUtc ? new Date(attendance.StartTimeUtc) : now}
        validate={validateEnteredBreakList}
        onConfirm={handleBreakEntryConfirm}
        onSkip={handleBreakEntrySkip}
      />

      <ConfirmationModal
        open={isConfirmingClockOut}
        onOpenChange={(open) => {
          setIsConfirmingClockOut(open);
          if (!open) setPendingBreakDetails([]);
          // The widget unmounts with the dialog; drop its token so a reopen asks for a fresh one.
          if (!open && needsCaptcha) resetCaptcha();
        }}
        title={t('CLOCK_OUT')}
        description={
          needsCaptcha ? (
            <span className="flex flex-col gap-3">
              <span>{t('CLOCK_OUT_CONFIRM_DESCRIPTION')}</span>
              {captchaSiteKey ? (
                <Captcha {...captcha} theme="light" size="normal" />
              ) : (
                <span className="text-error">{t('CAPTCHA_NOT_CONFIGURED')}</span>
              )}
            </span>
          ) : (
            t('CLOCK_OUT_CONFIRM_DESCRIPTION')
          )
        }
        onConfirm={handleClockOut}
        confirmLoading={isClockingOut}
        preventAutoClose
        confirmText="CLOCK_OUT"
        confirmClassName="bg-destructive text-destructive-foreground hover:bg-[#B4002A]"
      />
    </>
  );
};
